import React, { PureComponent } from 'react';
import { connect } from 'react-redux';

import { pvLanding } from 'events/landing';
import {
  evSearchProductStreamClick,
  evSearchProductStreamImpression
} from 'events/searchProductStream';

import { logErrorAndLogToServer } from 'middleware/logger';
import MartyErrorBoundary from 'components/common/MartyErrorBoundary';
import css from 'styles/containers/dealsForYou.scss';
import { PageLoader } from 'components/Loader';
import MelodyCardProduct from 'components/common/melodyCard/MelodyCardProduct';
import MelodyCarousel from 'components/common/MelodyCarousel';
import marketplace from 'cfg/marketplace.json';
import { variableBias } from 'apis/opal';
import { slashSearchProducts } from 'apis/calypso';
import { redirectToAuthenticationFor } from 'actions/redirect';
import { setDocMeta } from 'actions/meta';
import SiteAwareMetadata from 'components/SiteAwareMetadata';
import { titaniteView, track } from 'apis/amethyst';
import { uniqWith } from 'helpers/lodashReplacement';

const {
  defaultMeta: { separator, title },
  search: { msaMelodyImageParams }
} = marketplace;

export const getDefaultFilterGroups = () => [
  { zc1: 'Shoes' },
  { zc2: 'Shirts & Tops' },
  { zc2: 'Pants' },
  { zc1: 'Bags' },
  { zc1: 'Accessories' },
  { zc1: 'Jewelry' },
  { zc2: 'Coats & Outerwear' },
  { zc2: 'Sunglasses' },
  { zc1: 'Watches' }
];

const filtersToStr = filters =>
  Object.keys(filters)
    .map(filter => `${filter}/"${encodeURIComponent(filters[filter])}"`)
    .join('/');

const criteriaPathname = ({ filters, sort }) =>
  `/search/null/filter/${filtersToStr(filters)}/sort/${sort}`;

const searchPathCriteria = criteria => ({
  location: { pathname: criteriaPathname(criteria) },
  limit: criteria.limit || 15
});

const genderFilterMapper = (condition, gender) =>
  (condition
    ? criteria => Object.assign(criteria, { txAttrFacet_Gender: gender })
    : f => f);

const sortStr = hasBias =>
  (hasBias ? 'bestForYou/desc' : 'recentSalesStyle/desc');

const scoreDescSorter = (a, b) => b.score - a.score;

const criteriaId = criteriaPathname;

const withPercentOff = filters => ({ ...filters, percentOff: '[35 TO 100]' });

const filtersToCriteria = hasBias => filters => ({
  filters,
  sort: sortStr(hasBias)
});

export const MESSAGE_WITH_BIAS =
  'We’ve personalized these sale items based on your preferences. If these don’t match your tastes, please keep browsing, so we can keeping learning about what you like!';
export const MESSAGE_NO_BIAS =
  'Whoopsies! We can’t personalize your deals at this time. Keep browsing so we can learn more about what you like!';

export const GenderSelector = ({ hasBias, implicitGender, onSelectGender }) => {
  if (hasBias && implicitGender) {
    return null;
  }

  return (
    <div className={css.dealOptions}>
      <select name="gender" onChange={onSelectGender}>
        <option value="">Select Gender</option>
        {['Women', 'Men', 'Boys', 'Girls'].map(gender => (
          <option key={gender} value={gender}>
            {gender}
          </option>
        ))}
      </select>
    </div>
  );
};

export class ContentRow extends PureComponent {
  componentDidMount() {
    const { criteria, personalized, products } = this.props;
    track(evSearchProductStreamImpression, {
      products,
      personalized,
      criteria,
      searchProductStreamType: 'SALE_ITEMS',
      sourcePage: 'LANDING_PAGE'
    });
  }

  render() {
    const { criteria, personalized, products, title } = this.props;
    return (
      <div key={title} className={css.contentRow}>
        <h2>{title}</h2>
        <MelodyCarousel>
          {products.map((product, indexClicked) => (
            <MelodyCardProduct
              onComponentClick={() => {
                track(evSearchProductStreamClick, {
                  indexClicked,
                  product: products[indexClicked],
                  personalized,
                  criteria,
                  searchProductStreamType: 'SALE_ITEMS',
                  sourcePage: 'LANDING_PAGE'
                });
              }}
              key={product.styleId}
              msaImageParams={msaMelodyImageParams}
              cardData={product}
              alwaysShowOriginalPrice={true}
            />
          ))}
        </MelodyCarousel>
      </div>
    );
  }
}

export class ContentRowWrapper extends PureComponent {
  state = {
    response: null
  };

  componentDidMount = () => {
    const { criteria, searchProducts } = this.props;
    searchProducts(criteria)
      .then(resp => resp.json())
      .then(
        response => response?.results?.length > 0 && this.setState({ response })
      );
  };

  render() {
    const { criteria } = this.props;
    const { response } = this.state;
    if (!response) {
      return null;
    }

    const { bestForYouSortEligible, results } = response;
    const {
      filters: { zc1, zc2 }
    } = criteria;
    const title = zc2 || zc1;

    return (
      <ContentRow
        criteria={criteria}
        title={title}
        products={results}
        personalized={bestForYouSortEligible}
      />
    );
  }
}

export const DealsForYouContent = ({
  criteriaGroups,
  firstName,
  hasBias,
  implicitGender,
  onSelectGender,
  searchProducts
}) => (
  <div className={css.container}>
    <div className={css.box}>
      <h1>Deals for {firstName ? firstName : 'you'}</h1>
      <p>
        {hasBias ? MESSAGE_WITH_BIAS : MESSAGE_NO_BIAS} To return to this page,
        go through the Account menu.
      </p>
      <GenderSelector
        hasBias={hasBias}
        implicitGender={implicitGender}
        onSelectGender={onSelectGender}
      />
      <div className={css.rowWrapper}>
        {criteriaGroups.map(criteria => (
          <MartyErrorBoundary boundaryTag="DealsForYouRow">
            <ContentRowWrapper
              key={criteriaId(criteria)}
              criteria={criteria}
              searchProducts={searchProducts}
            />
          </MartyErrorBoundary>
        ))}
      </div>
    </div>
  </div>
);

export class DealsForYou extends PureComponent {
  state = {
    hasBias: false,
    criteriaGroups: [],
    implicitGender: '',
    explicitGender: ''
  };

  componentDidMount() {
    titaniteView();
    const {
      isRecognized,
      redirectToAuthenticationFor,
      setDocMeta
    } = this.props;

    if (isRecognized) {
      this.fetchCustomerPreferences();
      setDocMeta({ title: `Deals for You ${separator} ${title}` });
      track(pvLanding, { pageName: 'deals-for-you' });
    } else {
      redirectToAuthenticationFor(window.location);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    if (prevState.explicitGender !== this.state.explicitGender) {
      this.fetchCustomerPreferences();
    }
  }

  executeSearch = criteria => {
    const { appState, calypso } = this.props;
    return slashSearchProducts(
      calypso,
      searchPathCriteria(criteria),
      appState
    ).catch(() => {
      /* Not much we can do if a search fails... */
      logErrorAndLogToServer(
        `Error fetching search results for criteria: ${criteria}`
      );
    });
  };

  fetchCustomerPreferences = () => {
    const { fetchVariableBias, opal } = this.props;
    const { explicitGender } = this.state;

    return fetchVariableBias(opal)
      .then(r => r.json())
      .then(bias => {
        const hasBias = !!(bias && bias.attributes);

        // eslint-disable-next-line camelcase
        const genders = (bias?.attributes?.txAttrFacet_Gender || []).sort(
          scoreDescSorter
        );
        const implicitGender = genders?.length > 0 ? genders[0].value : '';

        const useExplicitGender = !implicitGender && !!explicitGender;
        const gender = implicitGender || explicitGender;

        const criteriaGroups = uniqWith(
          // eslint-disable-next-line camelcase
          (bias?.attributes?.txCategoryFacet_ZetaCategories2 || [])
            .sort(scoreDescSorter)
            .map(category => ({ zc2: category.value }))
            .concat(
              ['Women', 'Girls'].includes(gender) ? { zc2: 'Dresses' } : []
            )
            .concat(getDefaultFilterGroups())
            .map(withPercentOff)
            .map(genderFilterMapper(useExplicitGender, gender))
            .map(filtersToCriteria(hasBias)),
          criteriaId
        );

        this.setState({ criteriaGroups, hasBias, implicitGender });
      })
      .catch(() => {
        // could not get the VB, so let's just try for default categories.
        this.setState({
          hasBias: false,
          implicitGender: '',
          criteriaGroups: getDefaultFilterGroups()
            .map(withPercentOff)
            .map(genderFilterMapper(explicitGender, explicitGender))
            .map(filtersToCriteria(false))
        });
      });
  };

  handleSelectGender = e => {
    e.preventDefault();
    this.setState({ explicitGender: e.target.value });
  };

  render() {
    const { firstName } = this.props;
    const { criteriaGroups, hasBias, implicitGender } = this.state;

    if (criteriaGroups.length < 1) {
      // no data loaded yet
      return <PageLoader />;
    }

    return (
      <SiteAwareMetadata>
        <DealsForYouContent
          searchProducts={this.executeSearch}
          criteriaGroups={criteriaGroups}
          firstName={firstName}
          hasBias={hasBias}
          implicitGender={implicitGender}
          onSelectGender={this.handleSelectGender}
        />
      </SiteAwareMetadata>
    );
  }
}

const mapStateToProps = state => ({
  appState: { ...state, filters: { ...state.filters, bestForYou: true } }, // ensure enableBestForYou includes is passed for this page.
  calypso: state.environmentConfig.api.calypso,
  fetchVariableBias: variableBias,
  firstName: state?.holmes?.firstName,
  isRecognized: !!state.cookies['x-main'],
  opal: state.environmentConfig.api.opal
});

const mapDispatchToProps = {
  redirectToAuthenticationFor,
  setDocMeta
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(DealsForYou);
